/**
 * Player de áudio do defeito (anexo .webm Customers) no detalhe da OS.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Captions, Loader2, Pause, Play, Volume2 } from 'lucide-react';
import { Odv2HoverTooltip } from './Odv2HoverTooltip';
import { transcribeOrderDefectAudio } from '../services/boardOrderService';
import './OrderDetailsDefeitoAudio.css';

function formatTime(sec) {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const s = Math.floor(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

export function OrderDetailsDefeitoAudio({ src, orderId, onTranscribed }) {
  const audioRef = useRef(null);
  const seekRef = useRef(null);
  const scrubbingRef = useRef(false);
  const wasPlayingRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [transcribing, setTranscribing] = useState(false);

  useEffect(() => {
    setOpen(false);
    setPlaying(false);
    setCurrent(0);
    setDuration(0);
    setTranscribing(false);
    scrubbingRef.current = false;
  }, [src, orderId]);

  useEffect(() => {
    if (!open) return undefined;
    const el = audioRef.current;
    if (!el) return undefined;
    const onTime = () => {
      if (!scrubbingRef.current) setCurrent(el.currentTime || 0);
    };
    const onMeta = () => {
      if (Number.isFinite(el.duration) && el.duration > 0) setDuration(el.duration);
    };
    const onEnded = () => {
      if (!scrubbingRef.current) setPlaying(false);
    };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('durationchange', onMeta);
    el.addEventListener('ended', onEnded);
    el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    return () => {
      el.pause();
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('durationchange', onMeta);
      el.removeEventListener('ended', onEnded);
    };
  }, [open, src]);

  const timeFromClientX = (clientX) => {
    const track = seekRef.current;
    if (!track || !(duration > 0)) return 0;
    const rect = track.getBoundingClientRect();
    const ratio = rect.width > 0 ? clamp((clientX - rect.left) / rect.width, 0, 1) : 0;
    return ratio * duration;
  };

  const applySeek = (next) => {
    const audio = audioRef.current;
    const t = clamp(next, 0, duration > 0 ? duration : 0);
    setCurrent(t);
    if (!audio || !Number.isFinite(t)) return t;
    try {
      audio.currentTime = t;
    } catch {
      /* webm pode recusar seek até bufferizar */
    }
    return t;
  };

  const onSeekPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const audio = audioRef.current;
    wasPlayingRef.current = Boolean(audio && !audio.paused);
    if (audio && !audio.paused) audio.pause();
    scrubbingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    applySeek(timeFromClientX(e.clientX));
  };

  const onSeekPointerMove = (e) => {
    if (!scrubbingRef.current) return;
    e.preventDefault();
    setCurrent(timeFromClientX(e.clientX));
  };

  const onSeekPointerUp = (e) => {
    if (!scrubbingRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    applySeek(timeFromClientX(e.clientX));
    scrubbingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* já soltou */
    }
    const audio = audioRef.current;
    if (wasPlayingRef.current && audio) {
      audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const onSeekKeyDown = (e) => {
    if (!(duration > 0) || !audioRef.current) return;
    let next = current;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = current - 1;
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = current + 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = duration;
    else return;
    e.preventDefault();
    e.stopPropagation();
    next = clamp(next, 0, duration);
    setCurrent(next);
    audioRef.current.currentTime = next;
  };

  const seekPct = duration > 0 ? `${clamp((current / duration) * 100, 0, 100)}%` : '0%';

  const handleTranscribe = async () => {
    if (!orderId || transcribing) return;
    setTranscribing(true);
    try {
      const result = await transcribeOrderDefectAudio(orderId);
      if (!result.success) {
        alert(result.message || 'Erro ao transcrever áudio.');
        return;
      }
      if (typeof onTranscribed === 'function') {
        onTranscribed(result.data);
      }
    } catch {
      alert('Erro ao conectar com o servidor.');
    } finally {
      setTranscribing(false);
    }
  };

  if (!src) return null;

  return (
    <span
      className={`odv2-defeito-audio${open ? ' odv2-defeito-audio--open' : ''}`}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      <button
        type="button"
        className={`odv2-defeito-audio-btn${open ? ' odv2-defeito-audio-btn--active' : ''}`}
        aria-label={open ? 'Ocultar áudio do defeito' : 'Ouvir áudio do defeito'}
        aria-pressed={open}
        title="Áudio do defeito"
        onClick={() => setOpen((v) => !v)}
      >
        {!open ? <span className="odv2-defeito-audio-label">Defeito por áudio</span> : null}
        <span className="odv2-defeito-audio-icon" aria-hidden>
          <Volume2 size={13} strokeWidth={2.25} />
        </span>
      </button>
      {open ? (
        <span className="odv2-defeito-audio-row">
          <span className="odv2-defeito-audio-player">
            <audio ref={audioRef} src={src} preload="auto" />
            <button
              type="button"
              className="odv2-defeito-audio-play"
              aria-label={playing ? 'Pausar' : 'Reproduzir'}
              onClick={() => {
                const el = audioRef.current;
                if (!el) return;
                if (el.paused) {
                  el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
                } else {
                  el.pause();
                  setPlaying(false);
                }
              }}
            >
              {playing ? (
                <Pause size={12} strokeWidth={2.25} aria-hidden />
              ) : (
                <Play size={12} strokeWidth={2.25} aria-hidden />
              )}
            </button>
            <span className="odv2-defeito-audio-time">{formatTime(current)}</span>
            <span
              ref={seekRef}
              className="odv2-defeito-audio-seek"
              style={{ '--seek-pct': seekPct }}
              role="slider"
              tabIndex={0}
              aria-label="Progresso do áudio"
              aria-valuemin={0}
              aria-valuemax={duration > 0 ? Math.round(duration) : 0}
              aria-valuenow={Math.round(current)}
              aria-valuetext={`${formatTime(current)} de ${formatTime(duration)}`}
              onPointerDown={onSeekPointerDown}
              onPointerMove={onSeekPointerMove}
              onPointerUp={onSeekPointerUp}
              onPointerCancel={onSeekPointerUp}
              onKeyDown={onSeekKeyDown}
            >
              <span className="odv2-defeito-audio-seek-track" aria-hidden>
                <span className="odv2-defeito-audio-seek-fill" />
                <span className="odv2-defeito-audio-seek-thumb" />
              </span>
            </span>
            <span className="odv2-defeito-audio-time">{formatTime(duration)}</span>
          </span>
          {orderId ? (
            <Odv2HoverTooltip
              text="Transcrever áudio e adicionar nas observações da OS"
              placement="above"
              className="odv2-tooltip-wrap--inline-title"
            >
              <button
                type="button"
                className="odv2-defeito-audio-transcribe"
                aria-label="Transcrever áudio e adicionar nas observações da OS"
                disabled={transcribing}
                onClick={handleTranscribe}
              >
                {transcribing ? (
                  <Loader2 size={13} strokeWidth={2.25} className="odv2-defeito-audio-transcribe-spin" aria-hidden />
                ) : (
                  <Captions size={13} strokeWidth={2.25} aria-hidden />
                )}
                <span>{transcribing ? 'Transcrevendo…' : 'Transcrever'}</span>
              </button>
            </Odv2HoverTooltip>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
